import type { PayloadAction } from '@reduxjs/toolkit';
import type { SagaIterator } from 'redux-saga';
import { call, debounce, put, select, takeEvery } from 'redux-saga/effects';
import { v4 as uuidv4 } from 'uuid';

import { getLogger, httpClientInterface, isHttpServerError, requireEnvironmentConfig } from '../../utils/core';
import * as accountsActions from '../accounts/accountsActions';
import * as accountsBalancesActions from '../accounts-balances/accountsBalancesActions';
import * as businessActions from '../business/businessActions';
import * as categoriesActions from '../categories/categoriesActions';
import * as customersActions from '../customers/customersActions';
import * as projectsActions from '../projects/projectsActions';
import type { reportConfigurationsTypes } from '../report-configurations';
import { getReportConfigById } from '../report-configurations/reportConfigurationsSelectors';
import { transformReportConfigurationToRequestData } from '../report-configurations/reportConfigurationsTransformers';
import * as scheduledTransactionsActions from '../scheduled-transactions/scheduledTransactionsActions';
import * as tagsActions from '../tags/tagsActions';
import * as transactionsActions from '../transactions/transactionsActions';
import * as transactionsTransformers from '../transactions/transactionsTransformers';
import type { TransactionRecord } from '../transactions/transactionsTypes';
import { undoActions, undoTypes } from '../undo';
import { getActiveViewingConfigId, getEntity } from './generatedReportsSelectors';
import { generatedReportsActions as actions } from './generatedReportsSlice';
import * as transformers from './generatedReportsTransformers';
import type {
  ReconcileReportTransactionsPayload,
  ReportTransactionReconciliationOutcome,
} from './generatedReportsTypes';

const log = getLogger('app-components/flux/generated-reports/generatedReportsSagas.js');

const REPORT_GENERATION_ERROR_MESSAGE = 'We ran into a problem generating your report. Please try again later.';
const REPORT_TRANSACTION_FETCH_BATCH_SIZE = 50;
const REPORT_TRANSACTION_FETCH_CONCURRENCY = 4;
const activeReconciliations = new Set<string>();

type ReportTransactionFetchResult = {
  outcome: ReportTransactionReconciliationOutcome;
  resource?: TransactionRecord;
};

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

const isTransactionProps = (value: unknown): value is import('../transactions/transactionsTypes').TransactionProps =>
  isObject(value) &&
  typeof value.id === 'string' &&
  typeof value.amount === 'number' &&
  typeof value.postedOn === 'string' &&
  ['CASH_FLOW', 'INVESTMENT', 'INVOICE', 'PAYMENT'].includes(String(value.type));

const failedReportTransactionResults = (transactionIds: readonly string[]): ReportTransactionFetchResult[] =>
  transactionIds.map((id) => ({ outcome: { id, status: 'failed' } }));

async function fetchReportTransactionBatch(transactionIds: string[]): Promise<ReportTransactionFetchResult[]> {
  try {
    const requestUrl = `${requireEnvironmentConfig().services_url}/transactions`;
    const response = await httpClientInterface.send('GET', requestUrl, undefined, undefined, {
      query: `id=in=(${transactionIds.join(',')})`,
      limit: transactionIds.length,
    });
    if (!isObject(response.data) || !Array.isArray(response.data.resources)) {
      return failedReportTransactionResults(transactionIds);
    }

    const requestedIds = new Set(transactionIds);
    const transactionsById = new Map<string, TransactionRecord>();
    const failedIds = new Set<string>();
    for (const resource of response.data.resources) {
      if (!isObject(resource) || typeof resource.id !== 'string' || !requestedIds.has(resource.id)) continue;
      if (!isTransactionProps(resource)) {
        failedIds.add(resource.id);
        continue;
      }
      try {
        const transaction = transactionsTransformers.transformQcsTransactionToTransaction(resource);
        if (transaction) transactionsById.set(resource.id, transaction);
        else failedIds.add(resource.id);
      } catch {
        failedIds.add(resource.id);
      }
    }

    return transactionIds.map((id) => {
      const resource = transactionsById.get(id);
      if (resource) return { outcome: { id, status: 'found' }, resource };
      return { outcome: { id, status: failedIds.has(id) ? 'failed' : 'absent' } };
    });
  } catch {
    return failedReportTransactionResults(transactionIds);
  }
}

export async function fetchReportTransactionsBatched(
  transactionIds: string[],
): Promise<ReportTransactionFetchResult[]> {
  const batches: string[][] = [];
  for (let index = 0; index < transactionIds.length; index += REPORT_TRANSACTION_FETCH_BATCH_SIZE) {
    batches.push(transactionIds.slice(index, index + REPORT_TRANSACTION_FETCH_BATCH_SIZE));
  }
  const results: ReportTransactionFetchResult[][] = new Array(batches.length);
  let nextIndex = 0;
  const worker = async (): Promise<void> => {
    while (nextIndex < batches.length) {
      const index = nextIndex;
      nextIndex += 1;
      results[index] = await fetchReportTransactionBatch(batches[index]);
    }
  };
  await Promise.all(Array.from({ length: Math.min(REPORT_TRANSACTION_FETCH_CONCURRENCY, batches.length) }, worker));
  return results.flat();
}

type GenerateReportAction = PayloadAction<{
  location: string;
  configuration: reportConfigurationsTypes.ReportConfiguration;
  requestedAtVersion?: number;
}> & {
  meta?: {
    resolve?: (report: any) => void;
    reject?: (error: any) => void;
  };
};

export function* generateReport(action: GenerateReportAction): Generator<any, void, any> {
  const {
    payload: { location, configuration, requestedAtVersion },
    meta,
  } = action;
  const requestVersion = requestedAtVersion === undefined ? {} : { requestedAtVersion };
  log.debug('Generating report for', location);

  const requestUrl = `${requireEnvironmentConfig().services_url}/reports/generate`;
  const requestBody = transformReportConfigurationToRequestData(configuration);
  // TEMPORARY — remove once QCS accepts TIME for comparison reports. SIMPL-35032 finding 3.
  // Keep the agreed TIME contract in saved/synced state, but translate it at the request boundary
  // while the deployed service rejects TIME with QCS-0400-8 and accepts only NONE.
  if (requestBody?.reportType === 'COMPARISON') {
    requestBody.columnType = 'NONE';
    // Total-only comparisons do not need cumulative columns: their cumulative value is identical
    // to VALUE, and requesting it broadens the service contract for no user-visible benefit.
    if (requestBody.timestep != null && requestBody.timestep !== 'NONE') {
      requestBody.primaryAdditionalColumnTypes = Array.from(
        new Set([...(requestBody.primaryAdditionalColumnTypes ?? []), 'CUMULATIVE_VALUE' as const]),
      );
      requestBody.comparisonRanges = requestBody.comparisonRanges?.map((range) => ({
        ...range,
        additionalColumnTypes: Array.from(
          new Set([...(range.additionalColumnTypes ?? []), 'CUMULATIVE_VALUE' as const]),
        ),
      }));
    }
  }

  try {
    const response = requestBody
      ? yield call(httpClientInterface.send, 'POST', requestUrl, requestBody)
      : yield call(httpClientInterface.send, 'GET', requestUrl);
    log.debug('Generating report, response received for', location);

    const report = transformers.transformResponseToReport(response, configuration);
    yield put(actions.generateReportSuccess({ location, report, ...requestVersion }));
    meta?.resolve?.(report);
  } catch (error: unknown) {
    log.log('Received error', error, (error as any)?.response);
    if (isHttpServerError(error)) {
      yield put(
        undoActions.addUndo(
          undoTypes.mkUndo({
            userMessage: REPORT_GENERATION_ERROR_MESSAGE,
            key: uuidv4(),
          }),
        ),
      );
    }
    yield put(actions.generateReportFailure({ location, error: error as Error, ...requestVersion }));
    meta?.reject?.(error);
  }
}

export function* generateReportActionWatcher() {
  yield takeEvery(actions.generateReport, generateReport);
}

export function* reconcileReportTransactions(action: PayloadAction<ReconcileReportTransactionsPayload>): SagaIterator {
  const { attemptKey, generation, location, transactionIds } = action.payload;
  const reconciliationKey = `${location}:${generation}:${attemptKey}`;
  const entity: ReturnType<typeof getEntity> = yield select(getEntity, { location });
  if (
    activeReconciliations.has(reconciliationKey) ||
    entity?.generation !== generation ||
    entity.transactionReconciliations?.[attemptKey]?.status !== 'loading'
  ) {
    return;
  }

  activeReconciliations.add(reconciliationKey);
  try {
    const results: ReportTransactionFetchResult[] = yield call(fetchReportTransactionsBatched, transactionIds);
    const resources = results.flatMap(({ resource }) => (resource ? [resource] : []));
    const outcomes = results.map(({ outcome }) => outcome);
    yield put(
      actions.reconcileReportTransactionsSuccess({
        location,
        generation,
        attemptKey,
        transactionIds,
        resources,
        outcomes,
      }),
    );
    log.log('Report transaction reconciliation settled', {
      location,
      requestedCount: transactionIds.length,
      recoveredCount: resources.length,
      unresolvedCount: outcomes.length - resources.length,
    });
  } catch (error: unknown) {
    log.warn('Report transaction reconciliation failed', { location, requestedCount: transactionIds.length });
    yield put(
      actions.reconcileReportTransactionsFailure({
        location,
        generation,
        attemptKey,
        transactionIds,
        error: error instanceof Error ? error.message : 'Unable to reconcile report transactions',
      }),
    );
  } finally {
    activeReconciliations.delete(reconciliationKey);
  }
}

export function* reconcileReportTransactionsActionWatcher(): SagaIterator {
  yield takeEvery(actions.reconcileReportTransactions, reconcileReportTransactions);
}

const TRANSACTION_CHANGE_TYPES: string[] = [
  transactionsActions.updateTransactionSuccess.type,
  transactionsActions.batchTransactionsSuccess.type,
  transactionsActions.createTransactionSuccess.type,
  transactionsActions.deleteTransactionSuccess.type,
  transactionsActions.performTransactionActionSuccess.type,
  transactionsActions.applyTransactionsChanges.type,
];

const BALANCE_CHANGE_TYPES: string[] = [
  accountsBalancesActions.getAccountsBalancesSuccess.type,
  accountsBalancesActions.batchAccountsBalancesSuccess.type,
];

const BALANCE_SENSITIVE_SUBTYPES: string[] = ['NET_WORTH', 'SAVINGS', 'BALANCE_SHEET', 'BALANCES_OVER_TIME'];

const isTransactionChange = (action: { type: string }) => TRANSACTION_CHANGE_TYPES.includes(action.type);
const isBalanceChange = (action: { type: string }) => BALANCE_CHANGE_TYPES.includes(action.type);

function* regenerateActiveReport(): SagaIterator {
  const activeConfigId: string | null = yield select(getActiveViewingConfigId);
  if (!activeConfigId) return;

  const config: reportConfigurationsTypes.ReportConfiguration | undefined = yield select(getReportConfigById, {
    id: activeConfigId,
  });
  if (!config?.id) return;

  yield put(actions.generateReport({ location: config.id, configuration: config }));
}

function* regenerateActiveBalanceReport(): SagaIterator {
  const activeConfigId: string | null = yield select(getActiveViewingConfigId);
  if (!activeConfigId) return;

  const config: reportConfigurationsTypes.ReportConfiguration | undefined = yield select(getReportConfigById, {
    id: activeConfigId,
  });
  if (!config?.id || !BALANCE_SENSITIVE_SUBTYPES.includes(config.reportSubType)) return;

  yield put(actions.generateReport({ location: config.id, configuration: config }));
}

export function* regenerateActiveReportOnTransactionChangeWatcher() {
  yield debounce(300, isTransactionChange, regenerateActiveReport);
}

export function* regenerateActiveReportOnBalanceChangeWatcher() {
  yield debounce(300, isBalanceChange, regenerateActiveBalanceReport);
}

function* incrementDataVersionWorker() {
  yield put(actions.incrementDataVersion());
}

export function* dataVersionActionWatcher() {
  yield takeEvery(
    [
      // Categories
      categoriesActions.getCategoriesSuccess.type,
      categoriesActions.createCategorySuccess.type,
      categoriesActions.updateCategorySuccess.type,
      categoriesActions.deleteCategorySuccess.type,
      categoriesActions.batchCategoriesSuccess.type,
      categoriesActions.deleteAndReassignCategorySuccess.type,
      // Accounts
      accountsActions.getAccountsSuccess.type,
      accountsActions.createAccountSuccess.type,
      accountsActions.updateAccountSuccess.type,
      accountsActions.deleteAccountSuccess.type,
      accountsActions.batchAccountsSuccess.type,
      accountsActions.refreshAccountBalanceSuccess.type,
      // Transactions
      transactionsActions.getTransactionsSuccess.type,
      transactionsActions.createTransactionSuccess.type,
      transactionsActions.updateTransactionSuccess.type,
      transactionsActions.deleteTransactionSuccess.type,
      transactionsActions.batchTransactionsSuccess.type,
      // Tags
      tagsActions.getTagsSuccess.type,
      tagsActions.createTagSuccess.type,
      tagsActions.updateTagSuccess.type,
      tagsActions.deleteTagSuccess.type,
      tagsActions.batchTagsSuccess.type,
      // Business
      businessActions.getBusinessSuccess.type,
      businessActions.createBusinessSuccess.type,
      businessActions.updateBusinessSuccess.type,
      businessActions.deleteBusinessSuccess.type,
      businessActions.batchUpdateBusinessesSuccess.type,
      // Customers
      customersActions.getCustomersSuccess.type,
      customersActions.createCustomerSuccess.type,
      customersActions.updateCustomerSuccess.type,
      customersActions.deleteCustomerSuccess.type,
      // Scheduled Transactions
      scheduledTransactionsActions.getScheduledTransactionsSuccess.type,
      scheduledTransactionsActions.createScheduledTransactionSuccess.type,
      scheduledTransactionsActions.updateScheduledTransactionSuccess.type,
      scheduledTransactionsActions.deleteScheduledTransactionSuccess.type,
      scheduledTransactionsActions.batchScheduledTransactionsSuccess.type,
      // Projects
      projectsActions.getProjectSuccess.type,
      projectsActions.createProjectSuccess.type,
      projectsActions.updateProjectSuccess.type,
      projectsActions.deleteProjectSuccess.type,
      // Account Balances
      accountsBalancesActions.getAccountsBalancesSuccess.type,
      accountsBalancesActions.batchAccountsBalancesSuccess.type,
    ],
    incrementDataVersionWorker,
  );
}

export default [
  generateReportActionWatcher,
  reconcileReportTransactionsActionWatcher,
  dataVersionActionWatcher,
  regenerateActiveReportOnTransactionChangeWatcher,
  regenerateActiveReportOnBalanceChangeWatcher,
];
